import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { FunnelIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import Checkbox from "../form/Checkbox";

interface AccountTag {
  accountTagId: string;
  name: string;
}

interface Props {
  accountTags: AccountTag[];
  setShowAutoEnabled: (value: boolean | null) => void;
  accountTagIds: string[];
  setAccountTagIds: (accountTagIds: string[]) => void;
}

export default function TemplateArticleFilter({
  accountTags,
  setShowAutoEnabled,
  accountTagIds,
  setAccountTagIds,
}: Props) {
  const [auto, setAuto] = useState(false);
  const [single, setSingle] = useState(false);

  // Temporary state for pending changes
  const [tempAuto, setTempAuto] = useState(auto);
  const [tempSingle, setTempSingle] = useState(single);
  const [tempAccountTagIds, setTempAccountTagIds] =
    useState<string[]>(accountTagIds);

  useEffect(() => {
    if (auto && !single) {
      setShowAutoEnabled(true);
    } else if (!auto && single) {
      setShowAutoEnabled(false);
    } else {
      setShowAutoEnabled(null);
    }
  }, [auto, single, setShowAutoEnabled]);

  // Sync temp state when props change
  useEffect(() => {
    setTempAuto(auto);
    setTempSingle(single);
    setTempAccountTagIds(accountTagIds);
  }, [auto, single, accountTagIds]);

  const filtersActive = accountTagIds.length > 0 || auto || single;
  const filterCount = (auto ? 1 : 0) + (single ? 1 : 0) + accountTagIds.length;

  const handleTempTagFilter = (accountTagId: string) => {
    if (tempAccountTagIds?.includes(accountTagId)) {
      // Remove it if already present
      setTempAccountTagIds(
        tempAccountTagIds.filter((id: string) => id !== accountTagId)
      );
    } else {
      // Add it if not present
      setTempAccountTagIds([...(tempAccountTagIds || []), accountTagId]);
    }
  };

  const applyFilters = (closeMenu: () => void) => {
    setAuto(tempAuto);
    setSingle(tempSingle);
    setAccountTagIds(tempAccountTagIds);
    closeMenu();
  };

  const clearAllFilters = (closeMenu: () => void) => {
    setTempAccountTagIds([]);
    setTempAuto(false);
    setTempSingle(false);
    setAccountTagIds([]);
    setAuto(false);
    setSingle(false);
    closeMenu();
  };

  return (
    <div className="flex items-center gap-1">
      <Menu
        as="div"
        className="relative inline-block"
      >
        {({ close }) => (
          <>
            <MenuButton className="relative inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-white px-3 py-2 font-semibold text-gray-600 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50 text-xs tracking-wide">
              <FunnelIcon className="w-4 h-4" />
              {filtersActive && (
                <div className="w-4 h-4 bg-gray-200/80 rounded-full mr-1 flex items-center justify-center text-xs text-gray-600">
                  {filterCount}
                </div>
              )}
              <ChevronDownIcon
                aria-hidden="true"
                className="-mr-1 size-5 text-gray-400"
              />
            </MenuButton>

            <MenuItems
              transition
              className="absolute right-0 z-10 mt-2 w-72 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition"
            >
              <div className="py-1">
                {/* Type Section */}
                <div className="text-sm text-gray-500 ml-4 mt-2 py-2">Type</div>
                <MenuItem>
                  <div>
                    <div
                      className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100/80"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTempAuto(!tempAuto);
                      }}
                    >
                      <Checkbox
                        checked={tempAuto}
                        onChange={() => setTempAuto(!tempAuto)}
                      />
                      Auto Scheduled
                    </div>
                  </div>
                </MenuItem>
                <MenuItem>
                  <div>
                    <div
                      className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100/80"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTempSingle(!tempSingle);
                      }}
                    >
                      <Checkbox
                        checked={tempSingle}
                        onChange={() => setTempSingle(!tempSingle)}
                      />
                      Single
                    </div>
                  </div>
                </MenuItem>

                {accountTags?.length > 0 && (
                  <>
                    {/* Separator */}
                    <div className="w-full h-px bg-gray-200 my-2" />

                    {/* Tags Section */}
                    <div className="text-sm text-gray-500 ml-4 py-2">Tags</div>

                    {/* Scrollable Tags Container */}
                    <div className="max-h-48 overflow-y-auto">
                      {accountTags.map((tag: any) => {
                        return (
                          <MenuItem key={tag.accountTagId}>
                            <div className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100/80">
                              <div
                                className="flex items-center cursor-pointer gap-3 w-full"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTempTagFilter(tag.accountTagId);
                                }}
                              >
                                <Checkbox
                                  checked={tempAccountTagIds?.includes(
                                    tag.accountTagId
                                  )}
                                  onChange={() =>
                                    handleTempTagFilter(tag.accountTagId)
                                  }
                                />
                                {tag.name}
                              </div>
                            </div>
                          </MenuItem>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Separator */}
                <div className="w-full h-px bg-gray-200 my-2" />

                {/* Action Buttons */}
                <div className="flex justify-between items-center px-4 py-2 gap-2">
                  <button
                    className="text-sm text-gray-700 hover:text-gray-900"
                    onClick={() => clearAllFilters(close)}
                  >
                    Clear All
                  </button>
                  <button
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-1.5 rounded-md font-medium"
                    onClick={() => applyFilters(close)}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </MenuItems>
          </>
        )}
      </Menu>
    </div>
  );
}
